import React, { useState, useEffect } from "react";
import type { WorkflowTemplate, AuctionInfo, FastSnapshot } from "@auction-eth/shared-types";
import { api } from "../services/api.js";
import { getCurrentInstitutionId } from "../services/auth.js";

interface InitiateCasePageProps {
  onSuccess: (caseId: string) => void;
  onCancel: () => void;
}

type Phase = "fetch" | "review" | "creating";

interface FastData {
  loan_detail: Record<string, unknown>;
  borrower_info: Record<string, unknown>;
  asset_info: Record<string, unknown>;
  fetched_at: string;
  source: "fast_api" | "simulation" | "stub";
}

interface SimRecord {
  loan_ref: string;
  borrower_name: string;
  asset_category: string;
  asset_description: string;
  amount_etb: number;
  location_zone: string;
  branch: string;
}

const CURRENCY_OPTIONS = ["ETB", "USD", "EUR"];

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 40,
  padding: "0 12px",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  background: "var(--panel)",
  color: "var(--ink)",
  fontSize: 14,
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  color: "var(--muted)",
  marginBottom: 5,
};

const cardStyle: React.CSSProperties = {
  background: "var(--panel)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: "20px 24px",
  boxShadow: "var(--shadow)",
};

function SectionCard({ title, accent, children, disabled }: {
  title: string;
  accent?: string;
  children: React.ReactNode;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <div style={{
      ...cardStyle,
      borderLeft: `4px solid ${accent ?? "var(--brand)"}`,
      opacity: disabled ? 0.55 : 1,
      pointerEvents: disabled ? "none" : "auto",
    }}>
      <h3 style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: accent ?? "var(--brand)" }}>
        {title}
        {disabled && <span style={{ marginLeft: 8, fontSize: 10, background: "var(--border)", color: "var(--muted)", padding: "2px 8px", borderRadius: 999 }}>Populated by field agents</span>}
      </h3>
      {children}
    </div>
  );
}

function DataRow({ label, value }: { label: string; value: string | number | null | undefined }): React.ReactElement {
  const display = value !== null && value !== undefined && value !== "" ? String(value) : "—";
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
      <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--ink)", fontWeight: 500, maxWidth: "60%", textAlign: "right" }}>{display}</span>
    </div>
  );
}

export function InitiateCasePage({ onSuccess, onCancel }: InitiateCasePageProps): React.ReactElement {
  const [phase, setPhase] = useState<Phase>("fetch");
  const [loanRef, setLoanRef] = useState("");
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fastData, setFastData] = useState<FastData | null>(null);

  // Form state
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [memo, setMemo] = useState("");
  const [auctionInfo, setAuctionInfo] = useState<Partial<AuctionInfo>>({
    currency: "ETB",
    is_closed: false,
    bid_security_type: "FIXED",
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [simRecords, setSimRecords] = useState<SimRecord[]>([]);
  const [showSimPicker, setShowSimPicker] = useState(false);

  useEffect(() => {
    void api.templates.list().then((res) => {
      setTemplates(res.data ?? []);
      if (res.data && res.data.length > 0) setSelectedTemplate(res.data[0]!.template_id);
    });
    // Load simulation catalogue in background (gracefully fails in production)
    void api.fast.simCatalogue().then((res) => setSimRecords(res.records ?? [])).catch(() => undefined);
  }, []);

  async function handleFetch(): Promise<void> {
    if (!loanRef.trim()) return;
    setFetching(true);
    setFetchError(null);
    try {
      const res = await api.fast.lookup(loanRef.trim());
      setFastData(res.data);
      setPhase("review");
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : "Failed to fetch loan data from Collateral Registry.");
    } finally {
      setFetching(false);
    }
  }

  function updateAuction(field: keyof AuctionInfo, value: AuctionInfo[keyof AuctionInfo]): void {
    setAuctionInfo((prev) => ({ ...prev, [field]: value }));
  }

  async function handleCreate(): Promise<void> {
    if (!fastData || !selectedTemplate) return;
    setPhase("creating");
    setSubmitError(null);
    try {
      const assetInfo = fastData.asset_info;
      const fast_snapshot = {
        loan_detail: fastData.loan_detail as unknown as FastSnapshot["loan_detail"],
        borrower_info: fastData.borrower_info as unknown as FastSnapshot["borrower_info"],
        asset_info: fastData.asset_info as unknown as FastSnapshot["asset_info"],
        fetched_at: fastData.fetched_at,
      };
      const institutionId = getCurrentInstitutionId();
      if (!institutionId) {
        setSubmitError("Your account is not associated with an institution. Contact your administrator.");
        setPhase("review");
        return;
      }
      const res = await api.cases.create({
        template_id: selectedTemplate,
        linked_loan_id: loanRef.trim(),
        borrower_id: String(fastData.borrower_info["national_id"] ?? fastData.borrower_info["full_name"] ?? loanRef),
        lender_id: institutionId,
        asset_location: String(assetInfo["location_zone"] ?? ""),
        asset_category: String(assetInfo["asset_category"] ?? ""),
        fast_snapshot,
        auction_info: Object.keys(auctionInfo).length > 0 ? auctionInfo : undefined,
        initial_memo: memo.trim() || undefined,
      });
      onSuccess(res.data.case_id);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to create case.");
      setPhase("review");
    }
  }

  // ─── PHASE 1: Collateral Registry fetch ─────────────────────────────────────
  if (phase === "fetch") {
    return (
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <button onClick={onCancel} style={{ background: "none", border: "none", color: "var(--brand)", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 20 }}>
          ← Back
        </button>
        <h1 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 900, color: "var(--ink)" }}>Initiate Recovery Case</h1>
        <p style={{ margin: "0 0 32px", color: "var(--muted)", fontSize: 14 }}>
          Enter the loan reference ID to fetch the collateral record from Collateral Registry.
        </p>

        <div style={{ ...cardStyle }}>
          <div style={{ marginBottom: 20 }}>
            <label htmlFor="loan-ref-input" style={labelStyle}>Loan Reference ID *</label>
            <input
              id="loan-ref-input"
              type="text"
              placeholder="Your loan reference (dev simulations: see Help or pick below)"
              value={loanRef}
              onChange={(e) => setLoanRef(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void handleFetch(); }}
              style={{ ...inputStyle, height: 48, fontSize: 16 }}
              aria-label="Loan reference ID"
            />
          </div>

          {fetchError && (
            <div role="alert" style={{ background: "var(--danger-bg)", color: "var(--danger)", borderRadius: "var(--radius-sm)", padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>
              {fetchError}
            </div>
          )}

          <button
            onClick={() => void handleFetch()}
            disabled={fetching || !loanRef.trim()}
            aria-label="Fetch from Collateral Registry"
            style={{
              width: "100%",
              height: 48,
              borderRadius: "var(--radius-sm)",
              border: "none",
              background: fetching || !loanRef.trim() ? "var(--muted)" : "var(--brand)",
              color: "#fff",
              fontSize: 15,
              fontWeight: 700,
              cursor: fetching || !loanRef.trim() ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {fetching ? (
              <>
                <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                Fetching from Collateral Registry…
              </>
            ) : (
              "Fetch Loan Data from Collateral Registry →"
            )}
          </button>
        </div>

        {/* Simulation Picker — visible when sim catalogue is available */}
        {simRecords.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <button
              onClick={() => setShowSimPicker((v) => !v)}
              style={{ background: "none", border: "1px dashed var(--border)", color: "var(--muted)", fontSize: 13, fontWeight: 700, cursor: "pointer", borderRadius: "var(--radius-sm)", padding: "8px 16px", width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              aria-expanded={showSimPicker}
            >
              <span>🧪 Use simulation data ({simRecords.length} test loans available)</span>
              <span>{showSimPicker ? "▲" : "▼"}</span>
            </button>

            {showSimPicker && (
              <div style={{ border: "1px solid var(--border)", borderTop: "none", borderRadius: "0 0 var(--radius-sm) var(--radius-sm)", maxHeight: 380, overflowY: "auto" }}>
                {simRecords.map((r, i) => (
                  <button
                    key={r.loan_ref}
                    onClick={() => { setLoanRef(r.loan_ref); setShowSimPicker(false); }}
                    style={{
                      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                      width: "100%", padding: "12px 16px", border: "none", cursor: "pointer", textAlign: "left",
                      background: i % 2 === 0 ? "var(--panel)" : "var(--bg)",
                      borderBottom: "1px solid var(--border)",
                    }}
                    aria-label={`Select ${r.loan_ref}`}
                  >
                    <div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>{r.borrower_name}</p>
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--muted)" }}>{r.asset_description.slice(0, 60)}…</p>
                      <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--muted)" }}>{r.branch}</p>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, background: "var(--brand-subtle, #eff6ff)", color: "var(--brand)", padding: "2px 8px", borderRadius: 999, display: "block", marginBottom: 4 }}>{r.asset_category}</span>
                      <span style={{ fontSize: 12, color: "var(--ink)", fontWeight: 600 }}>ETB {r.amount_etb.toLocaleString()}</span>
                      <p style={{ margin: "2px 0 0", fontSize: 10, color: "var(--brand)", fontWeight: 700 }}>{r.loan_ref}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ─── PHASE 3: Creating ──────────────────────────────────────────────────────
  if (phase === "creating") {
    return (
      <div style={{ maxWidth: 560, margin: "0 auto", textAlign: "center", padding: "80px 20px" }}>
        <div style={{ width: 32, height: 32, border: "3px solid var(--border)", borderTopColor: "var(--brand)", borderRadius: "50%", animation: "spin 0.7s linear infinite", margin: "0 auto 20px" }} />
        <p style={{ color: "var(--muted)", fontSize: 16 }}>Creating case…</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ─── PHASE 2: Review & Enrich ───────────────────────────────────────────────
  const fd = fastData!;
  const loan = fd.loan_detail;
  const borrower = fd.borrower_info;
  const asset = fd.asset_info;

  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <button onClick={() => setPhase("fetch")} style={{ background: "none", border: "none", color: "var(--brand)", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: 0 }}>
          ← Back
        </button>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>Loan: <strong style={{ color: "var(--ink)" }}>{loanRef}</strong></span>
        {fd.source === "stub" && (
          <span style={{ fontSize: 10, background: "#fef3c7", color: "#b45309", padding: "2px 8px", borderRadius: 999, fontWeight: 700 }}>STUB DATA</span>
        )}
      </div>

      <h1 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 900, color: "var(--ink)" }}>Review Case Data</h1>
      <p style={{ margin: "0 0 28px", color: "var(--muted)", fontSize: 14 }}>
        Review the collateral data from Collateral Registry, set auction parameters, then create the case.
      </p>

      {submitError && (
        <div role="alert" style={{ background: "var(--danger-bg)", color: "var(--danger)", borderRadius: "var(--radius-sm)", padding: "12px 16px", marginBottom: 20, fontSize: 13 }}>
          {submitError}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

        {/* (a) Loan Detail */}
        <SectionCard title="Loan Detail" accent="#3b82f6">
          <DataRow label="Loan Reference" value={String(loan["loan_ref"] ?? "")} />
          <DataRow label="Amount" value={`${loan["currency"] ?? "ETB"} ${(loan["amount"] as number)?.toLocaleString() ?? "—"}`} />
          <DataRow label="Interest Rate" value={loan["interest_rate"] ? `${loan["interest_rate"]}%` : null} />
          <DataRow label="Disbursed At" value={loan["disbursed_at"] as string} />
          <DataRow label="Maturity Date" value={loan["maturity_date"] as string} />
          <DataRow label="Status" value={loan["status"] as string} />
          <DataRow label="Branch" value={loan["branch"] as string} />
        </SectionCard>

        {/* (b) Borrower Info */}
        <SectionCard title="Borrower Information" accent="#8b5cf6">
          <DataRow label="Full Name" value={borrower["full_name"] as string} />
          <DataRow label="National ID" value={borrower["national_id"] as string} />
          <DataRow label="Phone" value={borrower["phone"] as string} />
          <DataRow label="Account Number" value={borrower["account_number"] as string} />
          <DataRow label="Address" value={borrower["address"] as string} />
        </SectionCard>

        {/* (c) Asset Info — two sub-cards */}
        <div style={{ ...cardStyle, borderLeft: "4px solid #f59e0b" }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#f59e0b" }}>
            Asset Information
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* Collateral Registry record sub-card */}
            <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "14px 16px" }}>
              <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#f59e0b" }}>
                Collateral Registry Record
              </p>
              <DataRow label="Asset Type" value={asset["asset_type"] as string} />
              <DataRow label="Category" value={asset["asset_category"] as string} />
              <DataRow label="Description" value={asset["description"] as string} />
              <DataRow label="Serial / Plate" value={asset["serial_number_or_plate"] as string} />
              <DataRow label="Reg. Date" value={asset["registration_date"] as string} />
              <DataRow label="Value at Origination" value={asset["estimated_value_at_origination"] ? `ETB ${(asset["estimated_value_at_origination"] as number).toLocaleString()}` : null} />
              <DataRow label="Location Zone" value={asset["location_zone"] as string} />
              <DataRow label="Address" value={asset["address"] as string} />
            </div>
            {/* DAA enrichment sub-card — disabled at initiation */}
            <div style={{ background: "repeating-linear-gradient(45deg, var(--bg), var(--bg) 6px, var(--border) 6px, var(--border) 7px)", border: "1px dashed var(--border)", borderRadius: "var(--radius-sm)", padding: "14px 16px", opacity: 0.6 }}>
              <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--muted)" }}>
                Field Enrichment
              </p>
              <p style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6 }}>
                Physical condition, current location, valuation estimate, and photos will be submitted by DAA field agents and hub ops during the workflow.
              </p>
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                {["Physical Condition", "Current Location (lat/lng)", "Valuation Estimate", "Asset Photos"].map((f) => (
                  <div key={f} style={{ fontSize: 12, color: "var(--muted)", display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", border: "2px solid var(--muted)", flexShrink: 0 }} />
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* (d) Auction Info — editable */}
        <SectionCard title="Auction Parameters" accent="#10b981">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label htmlFor="ai-initial-price" style={labelStyle}>Initial Price</label>
              <input id="ai-initial-price" type="number" min={0} placeholder="e.g. 150000" style={inputStyle}
                value={auctionInfo.initial_price ?? ""}
                onChange={(e) => updateAuction("initial_price", e.target.value ? Number(e.target.value) : null)}
              />
            </div>
            <div>
              <label htmlFor="ai-currency" style={labelStyle}>Currency</label>
              <select id="ai-currency" style={{ ...inputStyle, cursor: "pointer" }}
                value={auctionInfo.currency ?? "ETB"}
                onChange={(e) => updateAuction("currency", e.target.value)}
              >
                {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="ai-start-time" style={labelStyle}>Auction Start Time</label>
              <input id="ai-start-time" type="datetime-local" style={inputStyle}
                value={auctionInfo.start_time?.slice(0, 16) ?? ""}
                onChange={(e) => updateAuction("start_time", e.target.value ? new Date(e.target.value).toISOString() : null)}
              />
            </div>
            <div>
              <label htmlFor="ai-end-time" style={labelStyle}>Auction End Time</label>
              <input id="ai-end-time" type="datetime-local" style={inputStyle}
                value={auctionInfo.end_time?.slice(0, 16) ?? ""}
                onChange={(e) => updateAuction("end_time", e.target.value ? new Date(e.target.value).toISOString() : null)}
              />
            </div>
            <div>
              <label htmlFor="ai-bid-security" style={labelStyle}>Bid Security Type</label>
              <select id="ai-bid-security" style={{ ...inputStyle, cursor: "pointer" }}
                value={auctionInfo.bid_security_type ?? "FIXED"}
                onChange={(e) => updateAuction("bid_security_type", e.target.value)}
              >
                <option value="FIXED">Fixed Amount</option>
                <option value="PERCENTAGE">Percentage</option>
              </select>
            </div>
            <div>
              <label htmlFor="ai-min-bid" style={labelStyle}>
                {auctionInfo.bid_security_type === "PERCENTAGE" ? "CPO Percentage Amount" : "Min. Amount to Bid"}
              </label>
              <input id="ai-min-bid" type="number" min={0} placeholder="e.g. 5000" style={inputStyle}
                value={auctionInfo.min_amount_to_bid ?? ""}
                onChange={(e) => updateAuction("min_amount_to_bid", e.target.value ? Number(e.target.value) : null)}
              />
            </div>
            <div>
              <label htmlFor="ai-min-increment" style={labelStyle}>Min. Increment</label>
              <input id="ai-min-increment" type="number" min={0} placeholder="e.g. 500" style={inputStyle}
                value={auctionInfo.min_increment ?? ""}
                onChange={(e) => updateAuction("min_increment", e.target.value ? Number(e.target.value) : null)}
              />
            </div>
            <div>
              <label htmlFor="ai-max-increment" style={labelStyle}>Max. Increment</label>
              <input id="ai-max-increment" type="number" min={0} placeholder="e.g. 5000" style={inputStyle}
                value={auctionInfo.max_increment ?? ""}
                onChange={(e) => updateAuction("max_increment", e.target.value ? Number(e.target.value) : null)}
              />
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={auctionInfo.is_closed ?? false}
                onChange={(e) => updateAuction("is_closed", e.target.checked)}
                style={{ width: 16, height: 16 }}
                aria-label="Sealed bidding"
              />
              <span style={{ fontSize: 13, color: "var(--ink)", fontWeight: 600 }}>
                Sealed Bidding (closed auction)
              </span>
              <span style={{ fontSize: 12, color: "var(--muted)" }}>— bids are private until auction closes</span>
            </label>
          </div>
        </SectionCard>

        {/* (e) Institution Memo */}
        <SectionCard title="Institution Memo" accent="#64748b">
          <label htmlFor="institution-memo" style={labelStyle}>Notes / Memo (optional)</label>
          <textarea
            id="institution-memo"
            placeholder="Any internal notes, conditions, or observations about this recovery case…"
            rows={4}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              background: "var(--bg)",
              color: "var(--ink)",
              fontSize: 14,
              fontFamily: "inherit",
              boxSizing: "border-box",
              resize: "vertical",
            }}
            aria-label="Institution memo"
          />
        </SectionCard>

        {/* Template selector */}
        <div style={cardStyle}>
          <label htmlFor="template-select" style={{ ...labelStyle, marginBottom: 8 }}>Workflow Template *</label>
          <select
            id="template-select"
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            style={{ ...inputStyle, height: 44, cursor: "pointer" }}
            required
            aria-label="Workflow template"
          >
            {templates.length === 0 && <option value="">Loading templates…</option>}
            {templates.map((t) => (
              <option key={t.template_id} value={t.template_id}>{t.flow_name}</option>
            ))}
          </select>
          <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--muted)" }}>
            The selected template defines the workflow steps, dispatch criteria, and module assignments.
          </p>
        </div>

        {/* Submit */}
        <button
          onClick={() => void handleCreate()}
          disabled={!selectedTemplate}
          aria-label="Create recovery case"
          style={{
            width: "100%",
            height: 52,
            borderRadius: "var(--radius-sm)",
            border: "none",
            background: !selectedTemplate ? "var(--muted)" : "var(--brand)",
            color: "#fff",
            fontSize: 16,
            fontWeight: 700,
            cursor: !selectedTemplate ? "not-allowed" : "pointer",
            transition: "transform 0.16s ease",
            marginBottom: 40,
          }}
          onMouseEnter={(e) => { if (selectedTemplate) (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = ""; }}
        >
          Initiate Recovery Case →
        </button>
      </div>
    </div>
  );
}
